import React, { createContext, useState, useContext } from 'react';
import { message } from 'antd';
import { createTest, updateTest } from '../helpers/apiCalls';

const TestBuilderContext = createContext();

export const useTestBuilder = () => useContext(TestBuilderContext);

// Base test configuration template
const createEmptyTest = () => ({
  title: '',
  platform: 'mobile',
  key: 'custom',
  plan: {
    steps: [],
    attempts: 1,
  },
  validation: {
    prompt: '',
    expected: [],
    unit: {
      type: '',
      values: [],
    },
    function: '',
  },
  code: '',
  reportName: '',
});

export const TestBuilderProvider = ({ children }) => {
  // Base test configuration template
  const createEmptyTest = () => ({
    title: '',
    platform: 'mobile',
    key: 'custom',
    plan: {
      steps: [],
      attempts: 1,
    },
    validation: {
      prompt: '',
      expected: [],
      unit: {
        type: '',
        values: [],
      },
      function: '',
    },
    code: '',
    reportName: '',
  });

  const [tests, setTests] = useState([]);
  const [applicableTo, setApplicableTo] = useState([]);

  // Updated state to support multilingual tests
  const [currentTest, setCurrentTest] = useState();

  // Track which language is currently being edited
  const [activeLanguage, setActiveLanguage] = useState('pt');

  // Removed separate steps state - now using currentTest.*.plan.steps
  const [activeTab, setActiveTab] = useState('basic');
  const [previewMode, setPreviewMode] = useState(false);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [expandedFields, setExpandedFields] = useState({});

  // Step management functions
  const handleStepTypeSelect = (type) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Get the current steps for all languages
      const currentSteps = updatedTest[activeLanguage].plan.steps;

      let newStep = {
        title: `Step ${currentSteps.length + 1}`,
        type: type,
        steps: [],
        stepsCovered: [],
        isCrutial: false,
      };

      if (type === 'form') {
        newStep.fields = [];
      } else if (type === 'table') {
        newStep.tableConfig = {
          rows: ['Linha 1'],
          columns: ['Coluna 1']
        };
      } else if (type === 'camera' || type === 'evidency') {
        newStep.timer = 0;
        newStep.interval = 0;
        newStep.expected = {
          hidden: true,
          calculateValue: '',
        };
        newStep.steps = [
          {
            title: 'Camera Step 1',
            description: {
              text: '',
              hints: [],
            },
          },
        ];
      }

      // Add the new step to all language versions
      Object.keys(updatedTest).forEach((lang) => {
        updatedTest[lang].plan.steps = [...updatedTest[lang].plan.steps, newStep];
      });

      return updatedTest;
    });
  };

  const handleAddStep = (type = 'form') => {
    handleStepTypeSelect(type);
  };

  const handleRemoveStep = (stepIndex) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Remove the step from all language versions
      Object.keys(updatedTest).forEach((lang) => {
        updatedTest[lang].plan.steps.splice(stepIndex, 1);
      });

      return updatedTest;
    });
  };

  // Updated to handle translatable step properties
  const handleStepChange = (stepIndex, field, value) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Fields that should be translated
      const translatableFields = ['title', 'instructions', 'options'];

      if (translatableFields.includes(field)) {
        // Store the original field for backward compatibility in the active language
        updatedTest[activeLanguage].plan.steps[stepIndex][field] = value;
      } else {
        // For non-translatable fields, update in all languages
        Object.keys(updatedTest).forEach((lang) => {
          updatedTest[lang].plan.steps[stepIndex][field] = value;
        });
      }

      return updatedTest;
    });
  };

  // Field management for form steps
  const handleAddField = (stepIndex) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Check if the step is a form step in the active language
      if (updatedTest[activeLanguage].plan.steps[stepIndex].type !== 'form') return prev;

      // Add the field to all language versions with language-specific options
      Object.keys(updatedTest).forEach((lang) => {
        const newField = {
          label: `Field ${updatedTest[activeLanguage].plan.steps[stepIndex].fields.length + 1}`,
          type: 'select',
          variant: 'single',
          required: true,
          options: [], // Base options field for backward compatibility
          tableConfig: {
            rows: [],
            columns: [],
            quickRows: 3,
            quickColumns: 3,
          },
          expected: { hidden: true, calculateValue: '', generateValidate: '' },
        };

        updatedTest[lang].plan.steps[stepIndex].fields.push(newField);
      });

      return updatedTest;
    });
  };

  const handleRemoveField = (stepIndex, fieldIndex) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Remove the field from all language versions
      Object.keys(updatedTest).forEach((lang) => {
        updatedTest[lang].plan.steps[stepIndex].fields.splice(fieldIndex, 1);
      });

      return updatedTest;
    });
  };

  // Updated to handle translatable field properties
  const handleFieldChange = (stepIndex, fieldIndex, field, value) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Fields that should be translated
      const translatableFields = ['label', 'placeholder', 'options'];

      if (translatableFields.includes(field)) {
        // Store the original field for backward compatibility in the active language
        updatedTest[activeLanguage].plan.steps[stepIndex].fields[fieldIndex][field] = value;
      } else {
        // For non-translatable fields, update in all languages
        Object.keys(updatedTest).forEach((lang) => {
          updatedTest[lang].plan.steps[stepIndex].fields[fieldIndex][field] = value;
        });
      }

      return updatedTest;
    });
  };

  const handleFieldExpectedChange = (stepIndex, fieldIndex, field, value) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Update expected field in all language versions
      Object.keys(updatedTest).forEach((lang) => {
        updatedTest[lang].plan.steps[stepIndex].fields[fieldIndex].expected[field] = value;
      });

      return updatedTest;
    });
  };
  const handleStepExpectedChange = (stepIndex, field, value) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Update expected field in all language versions
      Object.keys(updatedTest).forEach((lang) => {
        updatedTest[lang].plan.steps[stepIndex].expected[field] = value;
      });

      return updatedTest;
    });
  };

  // Camera step management
  const handleAddCameraStep = (stepIndex) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Check if the step is a camera step in the active language
      if (updatedTest[activeLanguage].plan.steps[stepIndex].type !== 'camera') return prev;

      const newCameraStep = {
        title: `Camera Step ${updatedTest[activeLanguage].plan.steps[stepIndex].steps.length + 1}`,
        description: {
          text: '',
          hints: [],
        },
      };

      // Add the camera step to all language versions
      Object.keys(updatedTest).forEach((lang) => {
        updatedTest[lang].plan.steps[stepIndex].steps.push(newCameraStep);
      });

      return updatedTest;
    });
  };

  const handleRemoveCameraStep = (stepIndex, cameraStepIndex) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Remove the camera step from all language versions
      Object.keys(updatedTest).forEach((lang) => {
        updatedTest[lang].plan.steps[stepIndex].steps.splice(cameraStepIndex, 1);
      });

      return updatedTest;
    });
  };

  // Updated to handle translatable camera step properties
  const handleCameraStepChange = (stepIndex, cameraStepIndex, field, value) => {
    setCurrentTest((prev) => {
      // Create a deep copy of the current test object
      const updatedTest = JSON.parse(JSON.stringify(prev));

      // Fields that should be translated
      const translatableFields = ['title', 'description'];
      if (translatableFields.includes(field)) {
        // Store the original field for backward compatibility in the active language
        updatedTest[activeLanguage].plan.steps[stepIndex].steps[cameraStepIndex][field] = value;
      } else {
        // For non-translatable fields like 'duration', update in all languages
        Object.keys(updatedTest).forEach((lang) => {
          updatedTest[lang].plan.steps[stepIndex].steps[cameraStepIndex][field] = value;
        });
      }

      return updatedTest;
    });
  };

  // Validation management - same for all languages
  const handleValidationChange = (field, value) => {
    setCurrentTest((prev) => {
      const updatedTest = { ...prev };

      // Apply the change to all languages
      Object.keys(updatedTest).forEach((lang) => {
        updatedTest[lang] = {
          ...updatedTest[lang],
          validation: {
            ...updatedTest[lang].validation,
            [field]: value,
          },
        };
      });

      return updatedTest;
    });
  };

  const handleValidationUnitChange = (field, value) => {
    setCurrentTest((prev) => {
      const updatedTest = { ...prev };

      // Apply the change to all languages
      Object.keys(updatedTest).forEach((lang) => {
        updatedTest[lang] = {
          ...updatedTest[lang],
          validation: {
            ...updatedTest[lang].validation,
            unit: {
              ...updatedTest[lang].validation.unit,
              [field]: value,
            },
          },
        };
      });

      return updatedTest;
    });
  };

  // Basic info management (with language support)
  const handleBasicInfoChange = (field, value) => {
    setCurrentTest((prev) => {
      const updatedTest = { ...prev };

      // First update the active language
      updatedTest[activeLanguage] = {
        ...updatedTest[activeLanguage],
        [field]: value,
      };

      // For non-translatable fields, update all languages
      const nonTranslatableFields = ['key', 'platform', 'code'];
      if (nonTranslatableFields.includes(field)) {
        Object.keys(updatedTest).forEach((lang) => {
          if (lang !== activeLanguage) {
            updatedTest[lang] = {
              ...updatedTest[lang],
              [field]: value,
            };
          }
        });
      }

      return updatedTest;
    });
  };

  // Save test configuration
  const saveTest = async (instance, testId = null) => {
    try {
      const isEditMode = !!testId;
      let result;


      const test = {
        stage: currentTest,
        applicableTo: applicableTo.map((c) => ({ category: c.id, type: c.kind ?? c.type })),
      };

      // Use the appropriate API function based on whether we're creating or updating
      if (isEditMode) {
        // Update existing test
        result = await updateTest(instance, testId, test);
        setTests((prev) => prev.map((t) => (t._id === testId ? result : t)));
        message.success('Test configuration updated successfully');
      } else {
        // Create new test
        result = await createTest(instance, test);
        setTests((prev) => [...prev, result]);
        message.success('Test configuration created successfully');
      }

      return true;
    } catch (error) {
      message.error('An error occurred while saving the test configuration');
      console.error(error);
      return false;
    }
  };

  const value = {
    createEmptyTest,
    tests,
    setTests,
    currentTest,
    applicableTo,
    setApplicableTo,
    setCurrentTest,
    activeLanguage,
    setActiveLanguage,
    // Removed steps and setSteps since they're now part of currentTest
    activeTab,
    setActiveTab,
    previewMode,
    setPreviewMode,
    expandedSteps,
    setExpandedSteps,
    expandedFields,
    setExpandedFields,
    handleStepTypeSelect,
    handleAddStep,
    handleRemoveStep,
    handleStepChange,
    handleAddField,
    handleRemoveField,
    handleFieldChange,
    handleFieldExpectedChange,
    handleStepExpectedChange,
    handleAddCameraStep,
    handleRemoveCameraStep,
    handleCameraStepChange,
    handleValidationChange,
    handleValidationUnitChange,
    handleBasicInfoChange,
    saveTest,
  };

  return <TestBuilderContext.Provider value={value}>{children}</TestBuilderContext.Provider>;
};
